import { Injectable } from '@angular/core';
import { RiskFormulaType } from '../models/risk-formula-type';
import { RiskEquationPart, RiskFormula } from '../models/risk-formula';
import { EquationPartTypes } from '../models/risk-equation-part-type';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { EntityType } from '../models/entity-type';
import { RiskTemplateTypes } from '../models/risk-template-types';
import { RiskFactor } from '../models/risk-factor';
import * as _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class AgilityRiskFormulaHelperService {

  operatorsKeys = {
    add: {
      keyCode: 43,
      key: '+',
      id: '2'
    },
    subtract: {
      keyCode: 45,
      key: '-',
      id: '3'
    },
    divide: {
      keyCode: 47,
      key: '/',
      id: '1'
    },
    multiply: {
      keyCode: 42,
      key: '*',
      id: '0'
    }
  };

  parenthesiseKeys = {
    leftParenth: {
      keyCode: 40,
      key: '(',
      id: 'leftParenth'
    },
    rightParenth: {
      keyCode: 41,
      key: ')',
      id: 'rightParenth'
    }
  };

  digitKeys = {
    0: {
      keyCode: 48,
      key: '0',
      id: '0'
    },
    1: {
      keyCode: 49,
      key: '1',
      id: '1'
    },
    2: {
      keyCode: 50,
      key: '2',
      id: '2'
    },
    3: {
      keyCode: 51,
      key: '3',
      id: '3'
    },
    4: {
      keyCode: 52,
      key: '4',
      id: '4'
    },
    5: {
      keyCode: 53,
      key: '5',
      id: '5'
    },
    6: {
      keyCode: 54,
      key: '6',
      id: '6'
    },
    7: {
      keyCode: 55,
      key: '7',
      id: '7'
    },
    8: {
      keyCode: 56,
      key: '8',
      id: '8'
    },
    9: {
      keyCode: 57,
      key: '9',
      id: '9'
    }
  };

  private onReloadFactorsSubject = new Subject<any>();
  private onFormulaChangedAndValidSubject = new Subject<RiskFormula>();

  constructor() {
  }

  getOperator(keyCode: number) {
    const field = Object.keys(this.operatorsKeys).find(ok => this.operatorsKeys[ok].keyCode === keyCode);
    if (!field) {
      return null;
    }
    return this.operatorsKeys[field];
  }

  getParenthesise(keyCode: string) {
    const field = Object.keys(this.parenthesiseKeys).find(ok => this.parenthesiseKeys[ok].keyCode === keyCode);
    if (!field) {
      return null;
    }
    return this.parenthesiseKeys[field];
  }

  getDigit(keyCode: string) {
    const field = Object.keys(this.digitKeys).find(ok => this.digitKeys[ok].keyCode === keyCode);
    if (!field) {
      return null;
    }
    return this.digitKeys[field];
  }

  getRiskFormulaAsArray(formula: RiskFormula): RiskEquationPart[] {
    return formula.formulaType === RiskFormulaType.multiPart ?
      this.getMultipartFormulaAsArray(formula) :
      formula.equation;
  }

  getMultipartFormulaAsArray(formula: RiskFormula): RiskEquationPart[] {
    let resultFormula = [{
      label: '(',
      type: EquationPartTypes.parenthesise,
      isEdit: false,
      order: 0,
      factorId: null
    }];
    resultFormula = resultFormula
      .concat(formula.formulas.find(f => f.formulaType === RiskFormulaType.impact).equation)
      .concat([
        {
          label: ')',
          type: EquationPartTypes.parenthesise,
          isEdit: false,
          order: 0,
          factorId: null
        }])
      .concat([formula.equation[1]])
      .concat([{
        label: '(',
        type: EquationPartTypes.parenthesise,
        isEdit: false,
        order: 0,
        factorId: null
      }])
      .concat(formula.formulas.find(f => f.formulaType === RiskFormulaType.probability).equation)
      .concat([{
        label: ')',
        type: EquationPartTypes.parenthesise,
        isEdit: false,
        order: 0,
        factorId: null
      }])
      .map(fp => fp);
    return resultFormula;
  }

  getFormulaAsString(formula: RiskFormula | Array<RiskEquationPart>) {

    const formArray = Array.isArray(formula) ? formula : this.getRiskFormulaAsArray(formula);

    const readonlyFormula = formArray.filter(fp => fp.label?.length).map(fp => fp.label).reduce((a, b) => {
      return a + b;
    });
    return readonlyFormula;
  }

  isLeftParenthesise(equationPart: RiskEquationPart) {
    return equationPart.type === EquationPartTypes.parenthesise &&
      equationPart.label === this.parenthesiseKeys.leftParenth.key;
  }

  isRightParenthesise(equationPart: RiskEquationPart) {
    return equationPart.type === EquationPartTypes.parenthesise &&
      equationPart.label === this.parenthesiseKeys.rightParenth.key;
  }

  triggerRefreshFactors(formulaId: string) {
    this.onReloadFactorsSubject.next(formulaId);
  }

  subscribeToRefreshFactor(callbackFunction: any): Subscription {
    return this.onReloadFactorsSubject.subscribe(callbackFunction);
  }

  triggerFormulaChangedAndValid(formula: RiskFormula) {
    this.onFormulaChangedAndValidSubject.next(formula);
  }

  subscribeToFormulaChangedAndValid(callbackFunction: any): Subscription {
    return this.onFormulaChangedAndValidSubject.pipe(debounceTime(1000)).subscribe(callbackFunction);
  }

  get targetTypesForSelect(): { value: EntityType | null; label: string }[] {
    const res = Object.keys(EntityType).map(key => {
      return {
        value: EntityType[key],
        label: key
      };
    });
    return res;
  }
  get targetTypesForTemplates(): { value: EntityType | null; label: string }[] {
    const res = Object.keys(EntityType).map(key => {
      return {
        value: EntityType[key],
        label: key
      };
    });
    return res.filter(type => type.value !== EntityType.user && type.value !== EntityType.role );
  }

  get templateTypesForSelect(): { value: RiskTemplateTypes | null; label: string }[] {
    const res = Object.keys(RiskTemplateTypes).map(key => {
      return {
        value: RiskTemplateTypes[key],
        label: key
      };
    });
    return res;
  }

  getFactorsUsedInFormula(formula: RiskFormula | Array<RiskEquationPart>, factors: RiskFactor[]) {

    const formArray = Array.isArray(formula) ? formula : this.getRiskFormulaAsArray(formula);
    const factorsInFormula = new Map();
    formArray.forEach(ep => {
      if (ep.type === EquationPartTypes.factor &&
        !factorsInFormula.has(ep.factorId)) {
        const factor = factors.find(fr => fr.id === ep.factorId);
        if (factor) {
          factorsInFormula.set(ep.factorId, _.cloneDeep(factor));
        }
      }
    });

    return [...factorsInFormula.values()];
  }
}
